import { Injectable } from '@angular/core';
import { PaginationResult } from '../../models/pagination-result';
import { OptionsValues } from '../../models/label-value';
import { RestClientService } from './rest-client.service';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

@Injectable()
export class BaseApiService<T> {
	constructor(protected _restClientService: RestClientService) {}

	public get apiUrl(): string {
		throw new Error('api url is not assign ⛔⛔');
	}

	public findById(id: string): Observable<T> {
		const url = this.apiUrl + '/';
		return this._restClientService.get(url + id).pipe(catchError(this.handleError));
	}

	public findPageableObjects(
		pageNumber: number,
		pageSize: number,
		sort: string | string[],
		searchObject: string | object
	): Observable<PaginationResult<T>> {
		searchObject = this.prepareSearchQuery(searchObject);
		const url =
			this.apiUrl +
			'?page=' +
			pageNumber +
			'&size=' +
			pageSize +
			'&query=' +
			searchObject +
			'&' +
			this.prepareSortQuery(sort);
		return this._restClientService.get(url).pipe(catchError(this.handleError));
	}

	protected prepareSortQuery(sort: string | string[]): string {
		const sortDef: string = 'sort=';
		if (typeof sort === 'string') {
			return sortDef + sort;
		} else {
			return sortDef + sort.join('&sort=');
		}
	}

	protected prepareSearchQuery(query: string | object): string {
		if (typeof query !== 'string') {
			return '';
		}
		return query;
	}

	public getSingle(searchObject?: string | object, relationNames?: string[], sort = 'creDate,DESC'): Observable<T> {
		searchObject = this.prepareSearchQuery(searchObject || '');
		searchObject += '&searchCriteriaOptions.recordCountRequired=false' + this.prepareDataGraphQuery(relationNames);
		return this.findPageableObjects(0, 1, sort, searchObject).pipe(map((result) => result.content?.[0] as T));
	}

	protected prepareDataGraphQuery(relationNames: string[] = []): string {
		let dataGraphQuery = '';
		if (relationNames) {
			relationNames.forEach((value, index) => {
				dataGraphQuery += `&searchCriteriaOptions.dataGraph[${index}].relationName=${value}`;
			});
		}
		return dataGraphQuery;
	}

	public createObject(object: object): Observable<T> {
		return this._restClientService.post(this.apiUrl, JSON.stringify(object)).pipe(catchError(this.handleError));
	}

	public updateObject(id: string, object: object): Observable<T> {
		return this._restClientService
			.put(this.apiUrl + '/' + id, JSON.stringify(object))
			.pipe(catchError(this.handleError));
	}

	public deleteObject(id: string): Observable<T> {
		return this._restClientService.delete(this.apiUrl + '/' + id).pipe(catchError(this.handleError));
	}

	public getList(searchArgs: string | object = '', size = 200, sort = 'creDate,ASC'): Observable<T[]> {
		return this.findPageableObjects(0, size, sort, searchArgs).pipe(map((result) => result.content || []));
	}

	public getOptionList({
		searchArgs = '',
		label = 'name',
		value = 'id',
		size = 200,
		sort = 'creDate,ASC',
	} = {}): Observable<OptionsValues> {
		return this.findPageableObjects(0, size, sort, searchArgs).pipe(
			map((result) => result.content || []),
			map((result) =>
				result.map((item: T) => {
					const row = item as unknown as Record<string, unknown>;
					return {
						label: String(row[label] ?? ''),
						value: row[value] as string | number | boolean,
					};
				})
			)
		);
	}

	protected handleError(error: Error): Observable<never> {
		return throwError(() => error);
	}
}
